import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  downloadProfitAndLossXlsx,
  type FinanceStatementResponse,
  getProfitAndLoss,
  type ReportBasis,
} from "@/lib/api";
import { downloadCsv, statementToCsv } from "./report-csv";
import { previousRange } from "./report-dates";
import { ReportBackButton } from "./ReportBackButton";
import { ReportError, ReportPeriod, StatementTable } from "./StatementTables";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

interface PlQuery {
  start: string;
  end: string;
  basis: ReportBasis;
  compareFrom: string;
  compareTo: string;
}

export function ProfitAndLossPage() {
  const [start, setStart] = useState(firstOfMonthIso);
  const [end, setEnd] = useState(todayIso);
  const [basis, setBasis] = useState<ReportBasis>("accrual");
  const [compareFrom, setCompareFrom] = useState("");
  const [compareTo, setCompareTo] = useState("");
  const [applied, setApplied] = useState<PlQuery>({
    start: firstOfMonthIso(),
    end: todayIso(),
    basis: "accrual",
    compareFrom: "",
    compareTo: "",
  });
  const [statement, setStatement] = useState<FinanceStatementResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);

  const run = useCallback((query: PlQuery) => {
    setLoading(true);
    setError(null);
    // A half-supplied comparison pair is passed through untouched — the
    // backend rejects it with a 400 (`resolve_pl_comparison` in
    // crate_surfaces.rs) and that error surfaces below via ReportError, so
    // the requirement is visible instead of silently dropped.
    getProfitAndLoss({
      start_date: query.start,
      end_date: query.end,
      basis: query.basis,
      ...(query.compareFrom ? { compare_from: query.compareFrom } : {}),
      ...(query.compareTo ? { compare_to: query.compareTo } : {}),
    })
      .then(setStatement)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!start || !end) {
      setError("Start and end dates are required.");
      return;
    }
    if (end < start) {
      setError("End date must be on or after the start date.");
      return;
    }
    // Only order-check a fully-supplied pair: a half-supplied pair must
    // reach the backend so its 400 surfaces via ReportError (see `run`).
    if (compareFrom && compareTo && compareTo < compareFrom) {
      setError("Compare end date must be on or after the compare start date.");
      return;
    }
    setApplied({ start, end, basis, compareFrom, compareTo });
  };

  const exportXlsx = async () => {
    // Dedicated profit-and-loss workbook twin
    // (`GET /reports/profit-and-loss.xlsx`) — the applied range, basis, and
    // comparison pair pass straight through.
    setExporting(true);
    setError(null);
    try {
      await downloadProfitAndLossXlsx({
        start_date: applied.start,
        end_date: applied.end,
        basis: applied.basis,
        ...(applied.compareFrom ? { compare_from: applied.compareFrom } : {}),
        ...(applied.compareTo ? { compare_to: applied.compareTo } : {}),
      });
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setExporting(false);
    }
  };

  // Fills the comparison bounds with the equal-length range immediately
  // before the typed range and runs it, so "previous period" needs no hand
  // typing.
  const compareToPrevious = () => {
    if (!start || !end || end < start) {
      setError("Enter a valid start and end date before comparing.");
      return;
    }
    const range = previousRange(start, end);
    if (!range) {
      setError("A valid start and end date are needed to compare periods.");
      return;
    }
    setCompareFrom(range.start);
    setCompareTo(range.end);
    setApplied({ start, end, basis, compareFrom: range.start, compareTo: range.end });
  };

  // Client-side CSV of the rows already on screen — no workbook endpoint.
  const exportCsv = () => {
    if (!statement) return;
    setCsvExporting(true);
    try {
      downloadCsv(
        `profit-and-loss-${applied.start}-${applied.end}.csv`,
        statementToCsv(statement, "Net income"),
      );
    } finally {
      setCsvExporting(false);
    }
  };

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Profit &amp; loss
          </h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Revenue and expenses for a date range, with net income at the bottom.
          </p>
          {statement ? <ReportPeriod statement={statement} /> : null}
        </div>
        <ReportBackButton />
      </div>

      {error ? (
        <ReportError message={error} onRetry={() => run(applied)} />
      ) : null}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
        <form
          onSubmit={submit}
          aria-label="Profit and loss filters"
          className="surface flex min-w-0 flex-wrap items-end gap-3 rounded-2xl p-5"
        >
          <label htmlFor="pl-start" className="grid gap-1 text-xs font-medium">
            Start date
            <Input
              id="pl-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
              disabled={loading}
            />
          </label>
          <label htmlFor="pl-end" className="grid gap-1 text-xs font-medium">
            End date
            <Input
              id="pl-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              required
              disabled={loading}
            />
          </label>
          <label htmlFor="pl-basis" className="grid gap-1 text-xs font-medium">
            Basis
            <select
              id="pl-basis"
              className="select"
              value={basis}
              onChange={(e) => setBasis(e.target.value as ReportBasis)}
              disabled={loading}
            >
              <option value="accrual">Accrual</option>
              <option value="cash">Cash</option>
            </select>
          </label>
          <label
            htmlFor="pl-compare-from"
            className="grid gap-1 text-xs font-medium"
          >
            Compare from (optional)
            <Input
              id="pl-compare-from"
              type="date"
              value={compareFrom}
              onChange={(e) => setCompareFrom(e.target.value)}
              disabled={loading}
            />
          </label>
          <label
            htmlFor="pl-compare-to"
            className="grid gap-1 text-xs font-medium"
          >
            Compare to (optional)
            <Input
              id="pl-compare-to"
              type="date"
              value={compareTo}
              onChange={(e) => setCompareTo(e.target.value)}
              disabled={loading}
            />
          </label>
          <Button type="submit" disabled={loading || !start || !end}>
            {loading ? "Running..." : "Run report"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={compareToPrevious}
            disabled={loading || !start || !end}
            title="Compare against the equal-length period immediately before this one"
          >
            Compare to previous period
          </Button>
          <p className="w-full text-xs text-[var(--muted-foreground)]">
            Comparison needs both bounds together — supplying only one is
            rejected by the API with a 400. The quick compare fills both with
            the preceding period of the same length.
          </p>
        </form>

        {/* Dedicated profit-and-loss workbook twin
            (`GET /reports/profit-and-loss.xlsx`) — sits with the filter row,
            not inside it. CSV is built from the rows already fetched. */}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={exportXlsx}
            disabled={exporting || loading}
            title="Download the profit & loss workbook (.xlsx) for the applied range"
          >
            {exporting ? "Exporting..." : "Export (XLSX)"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={exportCsv}
            disabled={csvExporting || loading || !statement}
            title="Download the profit & loss on screen as a CSV file"
          >
            {csvExporting ? "Exporting..." : "Export (CSV)"}
          </Button>
        </div>
      </div>

      {loading && statement === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : statement ? (
        <div
          className="table-scroll [&>.surface]:w-max [&>.surface]:min-w-full"
          role="region"
          aria-label="Profit and loss statement — scrolls sideways on narrow screens"
          tabIndex={0}
        >
          <StatementTable
            statement={statement}
            emptyMessage="No revenue or expenses in this period."
            grandTotalLabel="Net income"
            drillRange={{ start_date: applied.start, end_date: applied.end }}
          />
        </div>
      ) : null}
    </div>
  );
}
